/**
 * Designation Service
 * Handles designation management operations
 * Microservice: departmentmanagement
 * Domain: departments
 */
import { BaseService } from '../base/BaseService';
export class DesignationService extends BaseService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? DesignationService.DEFAULT_SERVICE_PATH,
            basePath: DesignationService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    // ==================== GET ALL ====================
    /**
     * Get all designations with optional filters
     * @param isActive - Optional filter for active/inactive designations
     * @param departmentId - Optional filter by department ID
     * @returns Array of designations
     */
    async getAll(isActive, departmentId) {
        const response = await this.getAllRaw(isActive, departmentId);
        return response.data ?? [];
    }
    /**
     * Get all designations with optional filters (raw response)
     * @param isActive - Optional filter for active/inactive designations
     * @param departmentId - Optional filter by department ID
     * @returns Raw response with array of designations
     */
    getAllRaw(isActive, departmentId) {
        const params = {};
        if (isActive !== undefined)
            params.isActive = isActive;
        if (departmentId !== undefined)
            params.departmentId = departmentId;
        const url = this.buildUrlWithParams('', params);
        return this.http.get(url);
    }
    // ==================== GET BY ID ====================
    /**
     * Get designation by ID
     * @param id - Designation ID
     * @returns Designation details
     */
    async getById(id) {
        const response = await this.getByIdRaw(id);
        if (!response.data) {
            throw new Error(`Designation with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get designation by ID (raw response)
     * @param id - Designation ID
     * @returns Raw response with designation details
     */
    getByIdRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.get(url);
    }
    // ==================== DESIGNATION TASKS ====================
    /**
     * Get all tasks for a designation
     * @param id - Designation ID
     * @returns Array of designation tasks
     */
    async getDesignationTasks(id) {
        const response = await this.getDesignationTasksRaw(id);
        return response.data ?? [];
    }
    /**
     * Get all tasks for a designation (raw response)
     * @param id - Designation ID
     * @returns Raw response with array of designation tasks
     */
    getDesignationTasksRaw(id) {
        const url = this.buildUrl(`${id}/tasks`);
        return this.http.get(url);
    }
    // ==================== DESIGNATION MEMBERS ====================
    /**
     * Get all members with this designation
     * @param id - Designation ID
     * @returns Array of member designations
     */
    async getDesignationMembers(id) {
        const response = await this.getDesignationMembersRaw(id);
        return response.data ?? [];
    }
    /**
     * Get all members with this designation (raw response)
     * @param id - Designation ID
     * @returns Raw response with array of member designations
     */
    getDesignationMembersRaw(id) {
        const url = this.buildUrl(`${id}/members`);
        return this.http.get(url);
    }
    /**
     * Assign a designation to a member
     * @param designationId - Designation ID
     * @param memberId - Member ID
     * @param data - Member assignment data
     * @returns Created or reactivated member designation
     */
    async assignMemberToDesignation(designationId, memberId, data) {
        const response = await this.assignMemberToDesignationRaw(designationId, memberId, data);
        if (!response.data) {
            throw new Error('Failed to assign member to designation');
        }
        return response.data;
    }
    /**
     * Assign a designation to a member (raw response)
     * @param designationId - Designation ID
     * @param memberId - Member ID
     * @param data - Member assignment data
     * @returns Raw response with created or reactivated member designation
     */
    assignMemberToDesignationRaw(designationId, memberId, data) {
        const url = this.buildUrl(`${designationId}/members/${memberId}`);
        return this.http.post(url, data);
    }
    /**
     * Remove a designation from a member (soft delete)
     * @param designationId - Designation ID
     * @param memberId - Member ID
     * @returns Success status
     */
    async removeDesignationFromMember(designationId, memberId) {
        const response = await this.removeDesignationFromMemberRaw(designationId, memberId);
        return response.success;
    }
    /**
     * Remove a designation from a member (raw response)
     * @param designationId - Designation ID
     * @param memberId - Member ID
     * @returns Raw response with success status
     */
    removeDesignationFromMemberRaw(designationId, memberId) {
        const url = this.buildUrl(`${designationId}/members/${memberId}`);
        return this.http.delete(url);
    }
    // ==================== CREATE ====================
    /**
     * Create a new designation
     * @param data - Designation creation data
     * @returns Created designation
     */
    async create(data) {
        const response = await this.createRaw(data);
        if (!response.data) {
            throw new Error('Failed to create designation');
        }
        return response.data;
    }
    /**
     * Create a new designation (raw response)
     * @param data - Designation creation data
     * @returns Raw response with created designation
     */
    createRaw(data) {
        const url = this.buildUrl();
        return this.http.post(url, data);
    }
    // ==================== UPDATE ====================
    /**
     * Update an existing designation
     * @param id - Designation ID
     * @param data - Designation update data
     * @returns Updated designation
     */
    async update(id, data) {
        const response = await this.updateRaw(id, data);
        if (!response.data) {
            throw new Error(`Failed to update designation with ID ${id}`);
        }
        return response.data;
    }
    /**
     * Update an existing designation (raw response)
     * @param id - Designation ID
     * @param data - Designation update data
     * @returns Raw response with updated designation
     */
    updateRaw(id, data) {
        const url = this.buildUrl(String(id));
        return this.http.put(url, data);
    }
    // ==================== DELETE ====================
    /**
     * Soft delete a designation
     * @param id - Designation ID
     * @returns Success status
     */
    async delete(id) {
        const response = await this.deleteRaw(id);
        return response.success;
    }
    /**
     * Soft delete a designation (raw response)
     * @param id - Designation ID
     * @returns Raw response with success status
     */
    deleteRaw(id) {
        const url = this.buildUrl(String(id));
        return this.http.delete(url);
    }
    // ==================== MODEL VIEWS ====================
    /**
     * Get designation ModelView by ID
     * @param id - Designation ID
     * @returns Designation view
     */
    async getDesignationView(id) {
        const response = await this.getDesignationViewRaw(id);
        if (!response.data) {
            throw new Error(`Designation view with ID ${id} not found`);
        }
        return response.data;
    }
    /**
     * Get designation ModelView by ID (raw response)
     * @param id - Designation ID
     * @returns Raw response with designation view
     */
    getDesignationViewRaw(id) {
        const url = this.buildUrl(`view/${id}`);
        return this.http.get(url);
    }
    /**
     * Get all designation ModelViews
     * @returns Array of designation views
     */
    async getAllDesignationViews() {
        const response = await this.getAllDesignationViewsRaw();
        return response.data ?? [];
    }
    /**
     * Get all designation ModelViews (raw response)
     * @returns Raw response with array of designation views
     */
    getAllDesignationViewsRaw() {
        const url = this.buildUrl('view');
        return this.http.get(url);
    }
    /**
     * Get designations by department ID
     * @param departmentId - Department ID
     * @returns Array of designation views for the department
     */
    async getDesignationsByDepartment(departmentId) {
        const response = await this.getDesignationsByDepartmentRaw(departmentId);
        return response.data ?? [];
    }
    /**
     * Get designations by department ID (raw response)
     * @param departmentId - Department ID
     * @returns Raw response with array of designation views
     */
    getDesignationsByDepartmentRaw(departmentId) {
        const url = this.buildUrl(`department/${departmentId}/view`);
        return this.http.get(url);
    }
    // ==================== CACHE ====================
    /**
     * Invalidate designation cache
     * @returns Success status
     */
    async invalidateCache() {
        const response = await this.invalidateCacheRaw();
        return response.success;
    }
    /**
     * Invalidate designation cache (raw response)
     * @returns Raw response with success status
     */
    invalidateCacheRaw() {
        const url = this.buildUrl('cache/invalidate');
        return this.http.post(url, {});
    }
}
DesignationService.DEFAULT_SERVICE_PATH = 'departments';
DesignationService.BASE_PATH = 'api/designation';
//# sourceMappingURL=DesignationService.js.map